/// FOURJS_START_COPYRIGHT(D,2016)
/// Property of Four Js*
/// (c) Copyright Four Js 2016, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("FrontCallService.modules.gbcqa", ["FrontCallService"], function (context, cls) {
  let _qaErrors = "";
  let _qaEventQueue = [];
  let _qaPlaying = false;

  /**
   * Helper to log an error
   * @param {*} message The message to log
   */
  function _qaLogError(message) {
    context.LogService.ClientQA.info(`ClientQA Error => ${message}`);
    if (_qaErrors.length === 0) {
      _qaErrors = message;
    } else {
      _qaErrors = `${_qaErrors}\n${message}`;
    }
  }

  /**
   * Helper to log a message
   * @param {*} message The message to log
   */
  function _qaLog(message) {
    context.LogService.ClientQA.log(message);
  }

  /**
   * Helper to build qaName from a domElement
   * @param {*} domElement The DOM element
   */
  function _qaBuildQAName(domElement) {
    if (!domElement) {
      return "no el";
    }

    let elementName = domElement.tagName;
    if (domElement.className !== "") {
      elementName += `:${domElement.className}`;
    }

    if (domElement.id !== "") {
      elementName += `:id(${domElement.id})`;
    }

    const auiId = domElement.dataset.auiId ?? null;
    if (auiId !== "") {
      elementName += `:iaui(${auiId})`;
    }

    return elementName;
  }

  /**
   * keyTab map containing keycode and key names
   */
  const _keytab = new Map([
    [
      "backspace",
      {
        key: "Backspace",
        keyCode: 8
      }
    ],
    [
      "tab",
      {
        key: "Tab",
        keyCode: 9
      }
    ],
    [
      "return",
      {
        key: "Enter",
        keyCode: 13
      }
    ],
    [
      "enter",
      {
        key: "Enter",
        keyCode: 13
      }
    ],
    [
      "esc",
      {
        key: "Escape",
        keyCode: 27
      }
    ],
    [
      "pageup",
      {
        key: "PageUp",
        keyCode: 33
      }
    ],
    [
      "pagedown",
      {
        key: "PageDown",
        keyCode: 34
      }
    ],
    [
      "end",
      {
        key: "End",
        keyCode: 35
      }
    ],
    [
      "home",
      {
        key: "Home",
        keyCode: 36
      }
    ],
    [
      "left",
      {
        key: "ArrowLeft",
        keyCode: 37
      }
    ],
    [
      "up",
      {
        key: "ArrowUp",
        keyCode: 38
      }
    ],
    [
      "right",
      {
        key: "ArrowRight",
        keyCode: 39
      }
    ],
    [
      "down",
      {
        key: "ArrowDown",
        keyCode: 40
      }
    ],
    [
      "ins",
      {
        key: "Insert",
        keyCode: 45
      }
    ],
    [
      "del",
      {
        key: "Delete",
        keyCode: 46
      }
    ],
    [
      "f1",
      {
        key: "F1",
        keyCode: 112
      }
    ],
    [
      "f2",
      {
        key: "F2",
        keyCode: 113
      }
    ],
    [
      "f3",
      {
        key: "F3",
        keyCode: 114
      }
    ],
    [
      "f4",
      {
        key: "F4",
        keyCode: 115
      }
    ],
    [
      "f5",
      {
        key: "F5",
        keyCode: 116
      }
    ],
    [
      "f6",
      {
        key: "F6",
        keyCode: 117
      }
    ],
    [
      "f7",
      {
        key: "F7",
        keyCode: 118
      }
    ],
    [
      "f8",
      {
        key: "F8",
        keyCode: 119
      }
    ],
    [
      "f9",
      {
        key: "F9",
        keyCode: 120
      }
    ],
    [
      "f10",
      {
        key: "F10",
        keyCode: 121
      }
    ],
    [
      "f11",
      {
        key: "F11",
        keyCode: 122
      }
    ],
    [
      "f12",
      {
        key: "F12",
        keyCode: 123
      }
    ]
  ]);

  /**
   * Obscure helper copied from ClientQA to build an ID
   * @param {*} d ID to build on
   * @param {String} attr attribute
   * @param {*} description
   * @returns {*} the new ID
   */
  function _qaExplicitHelper(d, attr, description) {
    if (attr === undefined || attr === "") {
      return d;
    }
    d = d.replace(" ", "_");
    d += description;
    d += attr;
    return d;
  }

  /**
   * Obscure helper to build logging of a given node
   * @param {*} node
   * @returns {string} log message
   */
  function _qaNodeDesc(node) {
    let d = `${node.getTag()}:${node.getId()}`;
    Object.keys(node._attributes).forEach(function (prop) {
      const attr = node.attribute(prop);
      if (attr) {
        d += `${prop}:'${attr}',`;
      }
    });
    return d;
  }

  /**
   * Obscure clientQA function to find recursively a given node by its name
   * @param {*} rootNode starting node ... if null, start from node0
   * @param {*} win Windows name ?
   * @param {*} name node name to look for ?
   * @param {*} colName column name to look for ?
   * @returns {*}
   */
  function _qaFindRecursive(rootNode, win, name, colName) {
    if (rootNode === null) {
      rootNode = gbc.SessionService?.getCurrent()?.getCurrentApplication()?.getNode(0);
      if (rootNode === null) {
        return null;
      }
    }

    _qaLog(`_qaFindRecursive: win:${win} name=${name}...`);

    const tag = rootNode.getTag();
    const wname = win ? win.attribute("name") : "";
    let d = "";
    let isFF = false;
    let rootNodeName = "";
    let rootNodeTxt = "";
    switch (tag) {
      case "Window":
        d = `${rootNode.attribute("name")}_${tag}`;
        win = rootNode;
        break;

      case "FormField":
        d = `${wname}_FormField_${rootNode.attribute("colName")}`;
        isFF = true;
        break;

      case "Value":
        d = `${wname}_Value_${colName}_${rootNode.getIndex()}`;
        break;

      case "ToolBarItem": //fallthru
      case "Button": //fallthru
      case "Action": //fallthru
      case "MenuAction":
        d = `${wname}_${tag}_${rootNode.attribute("name")}`;
        break;

      case "Table":
        d = `${wname}_${tag}_${rootNode.attribute("tabName")}`;
        break;

      default: //fallthru
        if (tag === "Matrix" || tag === "TableColumn") {
          colName = rootNode.attribute("colName");
        }

        rootNodeName = rootNode.attribute("name");
        rootNodeTxt = rootNode.attribute("text");

        if (!win) {
          d += "USERINTERFACE_";
          d += rootNodeTxt || rootNodeName;
        } else {
          d += wname;
        }
        d += `_${rootNode.getTag()}`;
        d = _qaExplicitHelper(d, rootNodeName, "_name_");
        d = _qaExplicitHelper(d, rootNode.attribute("tag"), "_tag_");
        d = _qaExplicitHelper(d, rootNodeTxt, "_txt_");
        break;
    }

    _qaLog(`_qaFindRecursive: d=${d}`);

    if (d === name) {
      _qaLog(`_qaFindRecursive: found:${name}-> ${_qaNodeDesc(rootNode)}`);
      return rootNode;
    }
    if (tag === "Group" && `${d}_GroupBox` === name) {
      _qaLog(`_qaFindRecursive: found groupbox:${name}-> ${_qaNodeDesc(rootNode)}`);
      return rootNode;
    }
    if (isFF && name.indexOf(d) === 0) {
      const part = name.substr(d.length);
      _qaLog(`_qaFindRecursive: found part:${part}in:${name}-> ${_qaNodeDesc(rootNode)}`);
      return rootNode;
    }

    // Not found, recursing in children...
    const children = rootNode.getChildren();
    for (const child of children) {
      const found = _qaFindRecursive(child, win, name, colName);
      if (found) {
        return found;
      }
    }

    // Not found
    return null;
  }

  /**
   * Helper to generate fake keyEvent from clientQA event
   * @param {*} key
   * @returns {*} key event or nothing
   */
  function _qaGenerateKeyEvent(key) {
    let init = {
      bubbles: true,
      view: window,
      cancelable: true,
      altKey: key.includes("alt+"),
      ctrlKey: key.includes("ctrl+"),
      shiftKey: key.includes("shift+"),
      metaKey: false,
      isComposing: false,
      repeat: false
    };

    const lastIndex = key.lastIndexOf("+");
    const lookupTerm = lastIndex < 1 ? key : key.slice(lastIndex + 1); // if key starts with +, do not slice it

    if (lookupTerm.length === 1) {
      // a simple letter
      init.key = lookupTerm;
      init.keyCode = lookupTerm.toUpperCase().codePointAt(0);
    } else {
      // a special key
      const keyEntry = _keytab.get(lookupTerm);
      if (keyEntry === undefined) {
        _qaLogError(`_qaGenerateKeyEvent: framework error: unsupported key: ${key}`);
        return;
      }
      init.key = keyEntry.lookupTerm;
      init.code = keyEntry.lookupTerm;
      init.keyCode = keyEntry.keyCode;
    }

    _qaLog(`_qaGenerateKeyEvent: generated keydown: key => ${init.key} [${init.keyCode}]`);

    return new KeyboardEvent("keydown", init); // jshint ignore:line
  }

  /**
   * Check if an element is in another
   * @param {*} elementToCheck
   * @param {*} containerElement
   * @returns {boolean}
   */
  function _qaCheckElementInclusion(elementToCheck, containerElement) {
    if (elementToCheck === null) {
      return false;
    }
    if (containerElement === null) {
      return false;
    }

    if (elementToCheck === containerElement) {
      return true;
    }

    for (const n of containerElement.childNodes) {
      if (_qaCheckElementInclusion(elementToCheck, n)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Helper to play events
   */
  function _qaPlayNextEvent() {
    _qaPlaying = true;

    if (_qaEventQueue.length === 0) {
      _qaLog("_qaPlayNextEvent: queue is empty");
      _qaPlaying = false;
      return;
    }

    const theEvent = _qaEventQueue.shift();
    _qaLog(`_qaPlayNextEvent: theEvent: key=<${theEvent.key}>, delay=${theEvent.delay}`);

    if (theEvent.key) {
      if (theEvent.key.startsWith("sleep:")) {
        // suspend execitoon for a given time
        theEvent.delay = theEvent.key.slice(6);
        _qaLog(`_qaPlayNextEvent: Forcing delay to ${theEvent.delay}`);
      } else if (theEvent.key === "mouse_click") {
        if (theEvent.clickTargetName) {
          _qaLog(`_qaPlayNextEvent: searching target for mouse click: ${theEvent.clickTargetName}`);
          const clickTargetNode = _qaFindRecursive(null, null, theEvent.clickTargetName, null);
          if (clickTargetNode) {
            theEvent.clickTarget = clickTargetNode.getWidget();
          }
        }
        if (theEvent.clickTarget) {
          // execute mouse click on a given target
          _qaLog(`_qaPlayNextEvent: playing mouse click on ${theEvent.clickTarget.getRootClassName()}`);
          gbc.SessionService.getCurrent()
            .getCurrentApplication()
            .scheduler.delayedMouseClickCommand(
              theEvent.clickTarget,
              new MouseEvent("click", {
                bubbles: true,
                cancelable: true,
                view: window,
                button: theEvent.mouseButton
              })
            );
        } else {
          _qaLogError("_qaPlayNextEvent: cannot play mouse_click: no target");
        }
      } else if (theEvent.key === "mouse_dblclick") {
        if (theEvent.clickTargetName) {
          _qaLog(`_qaPlayNextEvent: searching target for mouse click: ${theEvent.clickTargetName}`);
          const clickTargetNode = _qaFindRecursive(null, null, theEvent.clickTargetName, null);
          if (clickTargetNode) {
            theEvent.clickTarget = clickTargetNode.getWidget();
          }
        }
        if (theEvent.clickTarget) {
          // execute mouse double click on a given target
          _qaLog(`_qaPlayNextEvent: playing mouse dblclick on ${theEvent.clickTarget.getRootClassName()}`);
          // @note: need to check with @Thierry if we should have click and dblclick or only dblclick
          // In the second case, it looks weird that we did not manage dblclick command in the scheduler and I had to add it.
          gbc.SessionService.getCurrent()
            .getCurrentApplication()
            .scheduler.delayedMouseClickCommand(
              theEvent.clickTarget,
              new MouseEvent("click", {
                bubbles: true,
                cancelable: true,
                view: window,
                button: theEvent.mouseButton
              })
            );
          gbc.SessionService.getCurrent()
            .getCurrentApplication()
            .scheduler.delayedMouseClickCommand(
              theEvent.clickTarget,
              new MouseEvent("dblclick", {
                bubbles: true,
                cancelable: true,
                view: window,
                button: theEvent.mouseButton
              })
            );
        } else {
          _qaLogError("_qaPlayNextEvent: cannot play mouse_click: no target");
        }
      } else if (theEvent.key.startsWith("WAIT:")) {
        // Wait for a given widget to be on the UI
        const targetedWidget = theEvent.key.slice(5);
        const node = _qaFindRecursive(null, null, targetedWidget, null);
        if (!theEvent.repeated) {
          theEvent.repeated = 0;
        }
        if (node?.getWidget()?.getElement()) {
          _qaLog(`_qaPlayNextEvent: WAIT: widget [${targetedWidget}] present, let's continue.`);
          theEvent.delay = 0;
        } else if (theEvent.repeated > 10) {
          _qaLogError(`_qaPlayNextEvent: WAIT: widget [${targetedWidget}] NOT present for too long.`);
        } else {
          _qaLog(`_qaPlayNextEvent: WAIT: widget [${targetedWidget}] NOT present, let's wait.`);
          theEvent.repeated++;
          theEvent.delay = 1000; // force waiting for a second
          _qaEventQueue.unshift(theEvent);
        }
      } else if (theEvent.key.startsWith("WAITFOCUS:")) {
        // Wait for a given widget to be focussed
        const targetedWidget = theEvent.key.slice(10);
        const node = _qaFindRecursive(null, null, targetedWidget, null);
        if (!theEvent.repeated) {
          theEvent.repeated = 0;
        }
        if (node?.getWidget() && _qaCheckElementInclusion(document.activeElement, node.getWidget().getElement())) {
          _qaLog(`_qaPlayNextEvent: WAITFOCUS: widget [${targetedWidget}] has focus, let's continue.`);
          theEvent.delay = 0;
        } else if (theEvent.repeated > 10) {
          _qaLogError(`_qaPlayNextEvent: WAITFOCUS: widget [${targetedWidget}] did NOT get focus for too long.`);
        } else {
          _qaLog(`_qaPlayNextEvent: WAITFOCUS: widget [${targetedWidget}] NOT focused, let's wait.`);
          theEvent.repeated++;
          theEvent.delay = 1000; // force waiting for a second
          _qaEventQueue.unshift(theEvent);
        }
      } else if (theEvent.key.startsWith("WAITCLOSE:")) {
        // wait for a given widget to disappear
        const targetedWidget = theEvent.key.slice(10);
        const node = _qaFindRecursive(null, null, targetedWidget, null);
        if (!theEvent.repeated) {
          theEvent.repeated = 0;
        }
        if (node === null) {
          _qaLog(`_qaPlayNextEvent: WAITCLOSE: widget [${targetedWidget}] closed, let's continue.`);
          theEvent.delay = 0;
        } else if (theEvent.repeated > 10) {
          _qaLogError(`_qaPlayNextEvent: WAITCLOSE: widget [${targetedWidget}] present for too long.`);
        } else {
          _qaLog(`_qaPlayNextEvent: WAITCLOSE: widget [${targetedWidget}] still present, let's wait.`);
          theEvent.repeated++;
          theEvent.delay = 1000; // force waiting for a second
          _qaEventQueue.unshift(theEvent);
        }
      } else {
        // execute key input
        const keyEvent = _qaGenerateKeyEvent(theEvent.key);
        if (keyEvent) {
          _qaLog(`_qaPlayNextEvent: playing ${theEvent.key}`);
          gbc.SessionService.getCurrent().getCurrentApplication().scheduler.delayedKeyCommand(theEvent.key, keyEvent);
        } else {
          _qaLogError(`_qaPlayNextEvent: <${theEvent.key}> not implemented`);
        }
      }
    }

    _qaLog(`_qaPlayNextEvent: Delaying next event ${theEvent.delay}`);
    setTimeout(_qaPlayNextEvent, theEvent.delay);
  }

  /**
   * Bufferize events to play
   * @param {*} eventList
   * @param {*} interval
   */
  function _qaBufferizeEvents(eventList, interval) {
    if (eventList.length === 0) {
      return;
    }

    let eventLength = 0;

    if (eventList.charAt(0) === "<") {
      eventLength = eventList.indexOf(">") + 1;
      if (eventList.startsWith("<WAIT")) {
        _qaEventQueue.push({
          key: eventList.slice(1, eventLength - 1),
          delay: 0
        });
      } else {
        let key = eventList.slice(1, eventLength - 1).toLowerCase();
        key = key.replace("control", "ctrl");
        key = key.replace("escape", "esc");
        key = key.replace("return", "enter");
        key = key.replace("-", "+");
        _qaEventQueue.push({
          key: key,
          delay: interval
        });
      }
    } else if (eventList.charAt(0) === "(") {
      eventLength = eventList.indexOf(")") + 1;
      if (eventList.startsWith("(1->")) {
        _qaEventQueue.push({
          key: "mouse_click",
          delay: interval,
          clickTargetName: eventList.slice(4, eventLength - 1),
          mouseButton: 0
        });
      } else if (eventList.startsWith("(Left->")) {
        _qaEventQueue.push({
          key: "mouse_click",
          delay: interval,
          clickTargetName: eventList.slice(7, eventLength - 1),
          mouseButton: 0
        });
      } else {
        _qaLogError(`_qaBufferizeEvents: ${eventList} cannot be processed`);
        return;
      }
    } else {
      // manage default keys
      eventLength = 1;
      _qaEventQueue.push({
        key: eventList.charAt(0),
        delay: interval
      });
    }

    _qaBufferizeEvents(eventList.slice(eventLength), interval);
  }

  /**
   * Helper to convert int to hex
   * @param {*} intVal
   * @returns {string}
   */
  function _qaInt2Hex2(intVal) {
    if (typeof intVal === "string") {
      intVal = Number.parseInt(intVal, 10);
    }
    const hex = intVal.toString(16);
    //if we have one digit just add a 0
    const d2 = hex.length === 1 ? `0${hex}` : hex;
    return String(d2);
  }

  /**
   * From clientQA, compute color as hex value
   * @param {string} color
   * @returns {string} hex formatted color
   */
  function _qaHexColorString(color) {
    // Not need to compute color as hex
    if (color.startsWith("#")) {
      return color;
    }

    let red = 0,
      green = 0,
      blue = 0,
      _ = ""; // all regex match

    color = color.replace("!important", "").trim();

    if (color.startsWith("rgba")) {
      [_, red, green, blue] = /^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*(0|1|0?\.\d+)\)$/.exec(color);
    } else if (color.startsWith("rgb")) {
      [_, red, green, blue] = /^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/.exec(color);
    } else if (color === "white") {
      return "#ffffff";
    } else if (color === "black") {
      return "#000000";
    } else if (color === "transparent") {
      return "#000000"; //'rgba(0, 0, 0, 0)';
    }
    return `#${_qaInt2Hex2(red)}${_qaInt2Hex2(green)}${_qaInt2Hex2(blue)}`;
  }

  /**
   *
   * @param {*} domElement
   * @returns {string}
   */
  function _qaGetSolidBG(domElement) {
    while (domElement) {
      const color = globalThis.getComputedStyle(domElement)["background-color"];
      if (color !== "transparent" && color !== "rgba(0, 0, 0, 0)") {
        return _qaHexColorString(color);
      }
      domElement = domElement.parentNode;
    }
    return "__no_background_color__";
  }

  /**
   *
   * @param {*} what
   * @param {*} widgetName
   * @returns {string}
   */
  function _qaGetWidgetColor(what, widgetName) {
    let themeColorProperty = "ui-on-primary-color";
    switch (what) {
      case "textColor":
        themeColorProperty = "ui-text-icon-color";
        break;
      case "textBackgroundColor":
        themeColorProperty = "ui-field-color";
        break;
      case "highlightTextColor":
        themeColorProperty = "mt-list-currentrow-color";
        break;
      case "highlightBackgroundColor":
        themeColorProperty = "mt-list-currentrow-background-color";
        break;
      default:
        _qaLogError(`_qaGetWidgetColor: unmanaged attribute ${what}`);
    }

    // default color from theme
    let color = gbc.ThemeService.getValue(themeColorProperty);
    _qaLog(`_qaGetWidgetColor: ${what} => ${themeColorProperty} => ${color}`);

    if (widgetName) {
      const node = _qaFindRecursive(null, null, widgetName, null);
      if (node) {
        const styleColor = node.getStyleAttribute(what);
        if (styleColor !== null) {
          color = styleColor;
          _qaLog(`_qaGetWidgetColor: widget ${widgetName}: default value overwritten => ${color}`);
        }
      }
    }

    return _qaHexColorString(color);
  }

  /**
   * Frontcall service QA related module
   * @instance qa
   * @memberOf gbc.FrontCallService.modules
   */
  context.FrontCallService.modules.gbcqa = {
    /**
     * First function to call in a QA environement
     * @return {Array}
     */
    startqa: function () {
      _qaLog("FC / startqa");
      if (!globalThis.isSecureContext) {
        _qaLogError("ClientQA should be used in a secure context (https or localhost)");
      }
      return [""];
    },

    /**
     * From clientqa[QA] , get errors
     * @return {Array}
     */
    geterrors: function () {
      _qaLog("FC / geterrors");
      _qaLog(`_qaErrors=\n${_qaErrors}\n===`);
      const ret = _qaErrors;
      _qaErrors = "";
      return [ret];
    },

    /**
     * From clientqa, raise an error
     * @return {Array}
     */
    testraiseerror: function (message) {
      _qaLog(`FC / testraiseerror: message=${message}`);
      _qaLogError(message);
      gbc.SessionService.getCurrent().getCurrentApplication().scheduler.actionVMCommand(null, {
        actionName: "qa_error"
      });
      return [];
    },

    /**
     * From clientqa, cleaning qa event queue (aka remove all events from the current app)
     * @return {Array}
     */
    cleanup: function () {
      _qaLog("FC / cleanup");
      _qaEventQueue = [];
      return [];
    },

    /**
     * From clientqa, get the focused widget name
     * @return {Array}
     */
    getfocusedwidgetname: function () {
      _qaLog("FC / getfocusedwidgetname");

      const elementName = _qaBuildQAName(document.activeElement);

      _qaLog(`getfocusedwidgetname: elementName=${elementName}`);
      return [elementName];
    },

    /**
     * From clientqa, play user events
     * @return {Array}
     */
    playeventlist: function (eventList, delay, interval) {
      _qaLog(`FC / playeventlist: eventList=[${eventList}] delay=${delay} interval=${interval}`);

      _qaEventQueue.push({
        key: null,
        delay: delay
      });
      _qaBufferizeEvents(eventList, interval);

      if (!_qaPlaying) {
        _qaPlayNextEvent();
      }

      return [];
    },

    /**
     * getmatrixinfo
     */
    getmatrixinfo: function (matrixId, visibleRow, attrName) {
      _qaLog(`FC / getmatrixinfo: matrixId=${matrixId} visibleRow=${visibleRow} attrName=${attrName}`);

      const matrixNode = gbc.SessionService.getCurrent().getCurrentApplication().getNode(matrixId);
      const matrixName = matrixNode.attribute("name");
      const matrixFullName = matrixName.indexOf(".") === -1 ? `formonly.${matrixName}` : matrixName;

      _qaLog(`getmatrixinfo: matrixFullName=${matrixFullName}`);

      const domElement = document.querySelector(`[data-aui-name="${matrixName}"][data-gqa-index="${visibleRow}"] .gbc-label-text-container`);
      if (domElement) {
        let ret = null;
        switch (attrName) {
          case "text":
            ret = domElement.tagName === "INPUT" ? domElement.value : domElement.textContent;
            break;
          case "paletteBackgroundColor":
            ret = _qaGetSolidBG(domElement);
            break;
          case "paletteForegroundColor":
            ret = _qaHexColorString(globalThis.getComputedStyle(domElement).color);
            break;
          case "buttonEditImage":
            ret = "dummy"; // No way to retrieve the image label as the DVM may have replace it with char code in the fontawesome font.
            break;
          default:
            _qaLogError(`getmatrixinfo: attribute=${attrName} not yet implemented`);
            return [];
        }
        _qaLog(`getmatrixinfo: returning [${ret}]`);
        return [ret];
      }
      _qaLogError(`getmatrixinfo: cannot find ${matrixName}`);
      return [];
    },

    /**
     * Get an attribute according to its name
     */
    getattribute: function (typeOfObjectName, objectName, attributeName) {
      _qaLog(`FC / getattribute: typeOfObjectName=${typeOfObjectName} objectName=${objectName} attributeName=${attributeName}`);

      const node =
        typeOfObjectName === "id"
          ? gbc.SessionService.getCurrent().getCurrentApplication().getNode(objectName)
          : _qaFindRecursive(null, null, objectName, null);
      if (node === null) {
        _qaLogError(`getattribute: ${objectName} not found.`);
        return [];
      }

      const element = document.querySelector(`[data-aui-id="${node.getId()}"]`);

      if (element) {
        let width = null,
          height = null,
          textHolder = null,
          text = null,
          isFocused = null,
          img = null,
          cssBg = null,
          urlRegex = null,
          url = null,
          image = null;
        switch (attributeName) {
          case "width":
            width = element.getBoundingClientRect().width;
            _qaLog(`getattribute: ${objectName} width=${width}`);
            return [width];
          case "height":
            height = element.getBoundingClientRect().height;
            _qaLog(`getattribute: ${objectName} height=${height}`);
            return [height];
          case "text":
            textHolder = element.querySelector(".gbc-label-text-container") || element.querySelector(".gbc_dataContentPlaceholder") || element;
            text = (textHolder.value || textHolder.textContent).trim();
            _qaLog(`getattribute: ${objectName} text=${text}`);
            return [text];
          case "focus":
            isFocused = _qaCheckElementInclusion(document.activeElement, element) ? 1 : 0;
            _qaLog(`getattribute: ${objectName}  isFocused=${isFocused}`);
            return [isFocused];
          case "image":
            img = element.querySelector("img");
            cssBg = globalThis.getComputedStyle(element).backgroundImage;
            urlRegex = /url\("(.*)"\)/.exec(cssBg);
            url = urlRegex && urlRegex.length >= 1 ? urlRegex[1] : "";
            image = img?.attributes?.src?.value ? img.attributes.src.value : url;
            _qaLog(`getattribute: ${objectName}  image=${image}`);
            return [image];
          default:
            _qaLogError(`getattribute: attribute ${attributeName} not implemented.`);
            return [];
        }
      }
      return [];
    },

    /**
     * Get an attribute according to its name
     */
    getinformation: function (what, arg) {
      let returnValue = [];
      _qaLog(`FC / getinformation: what=${what} arg=${arg}`);

      switch (what) {
        case "textColor":
        case "textBackgroundColor":
        case "highlightTextColor":
        case "highlightBackgroundColor":
          returnValue = [_qaGetWidgetColor(what, null)];
          break;

        case "universalClient":
        case "universalRendering":
          returnValue = [1];
          break;

        case "QTFE_GIT_VERSION":
        case "qtVersionStr":
        case "controllerType":
        case "release_compiled":
        case "styleClass":
        case "styleName": // QT widgets style => undefined
        case "browserVersion": // we may answer correctly here ...
        case "osVersion": // we may answer correctly here ...
        case "VMVersion": // we may answer correctly here ...
          returnValue = ["undefined"];
          break;

        case "isIPad":
          returnValue = [navigator.appVersion.indexOf("iPad") !== -1];
          break;
      }

      _qaLog(`getinformation: returning [${returnValue}]`);
      return returnValue;
    },

    /**
     *
     * @param {*} tableName
     * @param {*} tableId
     * @param {*} visibleRow
     * @returns {*}
     */
    checktableishighlighted: function (tableName, tableId, visibleRow) {
      _qaLog(`FC / checktableishighlighted: tableName=${tableName} tableId=${tableId} visibleRow=${visibleRow}`);

      const highlightBackgroundColor = _qaHexColorString(gbc.ThemeService.getValue("gbc-TableWidget-currentRow-background-color"));
      const tableNode = gbc.SessionService.getCurrent().getCurrentApplication().getNode(tableId);
      const firstColumnName = tableNode.getChildren()[0].attribute("colName");
      const firstColumnFullName = firstColumnName.indexOf(".") === -1 ? `formonly.${firstColumnName}` : firstColumnName;
      const domElement = document.querySelector(`[data-aui-name="${firstColumnFullName}"][data-gqa-index="${visibleRow}"] .gbc-label-text-container`);
      const cellBackgroundColor = _qaGetSolidBG(domElement);
      _qaLog(
        `checktableishighlighted: returning (cellBackgroundColor=${cellBackgroundColor}) == (highlightBackgroundColor=${highlightBackgroundColor})`
      );
      return [cellBackgroundColor === highlightBackgroundColor];
    },

    /**
     *
     * @param {*} tableName
     * @param {*} tableId
     * @param {*} columnId
     * @param {*} attributeName
     * @returns {*}
     */
    gettablecolumninfo: function (tableName, tableId, columnId, attributeName) {
      _qaLog(`FC / gettablecolumninfo: tableName=${tableName} tableId=${tableId} columnId=${columnId} attributeName=${attributeName}`);

      const tableNode = gbc.SessionService.getCurrent().getCurrentApplication().getNode(tableId);
      const auiName = tableNode.attribute("name");
      const tableDomElement =
        document.querySelector(`[data-aui-name="${auiName}"][data-gqa-aui-id="${tableId}"]`) ||
        document.querySelector(`[data-aui-name="${tableName}"][data-gqa-aui-id="${tableId}"]`);
      const columnNode = gbc.SessionService.getCurrent().getCurrentApplication().getNode(columnId);
      const columnIndex = columnNode.getIndex();
      const columnHeaderElement = tableDomElement.querySelector(
        ".gbc_TableHeaderGroup .gbc_RTableRowWidget" + ` .gbc_RTableColumnWidget:nth-child(${columnIndex + 1})`
      );

      let isInDom = null,
        columnWidget = null;
      switch (attributeName) {
        case "text":
          if (!columnHeaderElement) {
            _qaLogError("gettablecolumninfo: column not found");
            return [];
          }
          const headerText = columnHeaderElement.querySelector(".headerText").textContent;
          _qaLog(`gettablecolumninfo: returning headerText=${headerText}`);
          return [headerText];

        case "visible":
          isInDom = false;
          columnWidget = columnNode.getController().getWidget();
          if (columnWidget?.isDetachedFromDom) {
            isInDom = !columnWidget.isDetachedFromDom();
          }
          _qaLog(`gettablecolumninfo: returning isInDom=${isInDom}`);
          return [isInDom ? "1" : "0"];

        default:
          _qaLogError(`gettablecolumninfo: [${attributeName}] not implemented.`);
          return [];
      }
    },

    /**
     *
     * @param {*} tabName
     * @param {*} colId
     * @param {*} vId
     * @param {*} attrName
     * @returns {*}
     */
    getTableAttributeById: function (tableName, columnId, valueId, attributeName) {
      _qaLog(`FC / getTableAttributeById: tableName=${tableName} columnId=${columnId} valueId=${valueId} attributeName=${attributeName}`);

      const columnNode = gbc.SessionService.getCurrent().getCurrentApplication().getNode(columnId);
      if (!columnNode) {
        _qaLogError("gettablecolumninfo: cannot find column node");
        return [];
      }

      const columnName = columnNode.attribute("name");

      const valueNode = gbc.SessionService.getCurrent().getCurrentApplication().getNode(valueId);
      if (!valueNode) {
        _qaLogError("gettablecolumninfo: cannot find value node");
        return [];
      }

      const visibleRow = valueNode.getIndex();
      const domElement = document.querySelector(`[data-aui-name="${columnName}"][data-gqa-index="${visibleRow}"] .gbc-label-text-container`);
      if (!domElement) {
        _qaLogError("gettablecolumninfo: cannot find DOM element for the given table cell");
        return [];
      }

      let elementText = null,
        bgColor = null,
        fgColor = null;
      switch (attributeName) {
        case "text":
          elementText = domElement.tagName === "INPUT" ? domElement.value : domElement.textContent;
          _qaLog(`gettablecolumninfo: returning [${elementText}]`);
          return [elementText];
        case "paletteBackgroundColor":
          bgColor = _qaGetSolidBG(domElement);
          _qaLog(`gettablecolumninfo: returning [${bgColor}]`);
          return [bgColor];
        case "paletteForegroundColor":
          fgColor = _qaHexColorString(globalThis.getComputedStyle(domElement).color);
          _qaLog(`gettablecolumninfo: returning [${fgColor}]`);
          return [fgColor];
        default:
          _qaLogError(`gettablecolumninfo: attributeName=${attributeName} not yet implemented`);
          return [];
      }
    },

    /**
     * Click on a table cell
     * @param {*} tableName
     * @param {*} tableId
     * @param {*} columnId
     * @param {*} visibleRow
     * @param {*} button
     * @returns {*}
     */
    clicktablecell: function (tableName, tableId, columnId, visibleRow, mouseButton) {
      _qaLog(
        `FC / getTableAttributeById: tableName=${tableName} tableId=${tableId} columnId=${columnId} visibleRow=${visibleRow} mouseButton=${mouseButton}`
      );

      const columnNode = gbc.SessionService.getCurrent().getCurrentApplication().getNode(columnId);

      const clickTarget = columnNode.getController().getWidget().getChild(visibleRow).getWidget();

      const eventKey = mouseButton.startsWith("Double") ? "mouse_dblclick" : "mouse_click";
      let buttonNumber = -1;
      switch (mouseButton) {
        case "1":
        case "Left":
        case "Double-1":
        case "DoubleLeft":
          buttonNumber = 0;
          break;

        case "2":
        case "Mid":
          buttonNumber = 1;
          break;

        case "3":
        case "Right":
        case "Context":
          buttonNumber = 2;
          break;
        default:
          _qaLogError(`clicktablecell: mouseButton=${mouseButton} not yet implemented`);
          return [];
      }

      _qaEventQueue.push({
        key: eventKey,
        delay: 20,
        clickTarget: clickTarget,
        mousebutton: buttonNumber
      });

      if (!_qaPlaying) {
        _qaPlayNextEvent();
      }

      return [];
    },

    /**
     *
     * @param {*} tabName
     * @param {*} tabId
     */
    gettablefocuscolumnrow: function (tableName, tableId) {
      _qaLog(`FC / gettablefocuscolumnrow: tableName=${tableName} tableId=${tableId}`);
      const focusElement = document.activeElement;
      if (focusElement.tagName === "INPUT") {
        const parentElement = focusElement.parentElement;
        const columnName = parentElement.dataset.auiName ?? null;
        const columnId = parentElement.dataset.auiId ?? null;

        const columnNode = gbc.SessionService.getCurrent().getCurrentApplication().getNode(columnId);
        if (columnNode === null || columnNode.getTag() !== "Value") {
          _qaLogError("gettablefocuscolumnrow: unable to get the column node");
          return [];
        }

        const valueListNode = columnNode.getParentNode();
        const tableColumnNode = valueListNode.getParentNode();
        const tableNode = tableColumnNode.getParentNode();

        const rowNumber = columnNode.getIndex() + Number.parseInt(tableNode.attribute("offset"), 10);

        const ret = (columnName.startsWith("formonly.") ? columnName.slice(9) : columnName) + rowNumber;
        _qaLog(`gettablefocuscolumnrow: returning [${ret}]`);
        return [ret];
      }
      _qaLogError("gettablefocuscolumnrow: display array not yet implemented");
      return [];
    },

    /**
     *
     */
    removestoredsettings: function () {
      _qaLog("FC / removestoredsettings...");

      gbc.StoredSettingsService.reset();
      return [""];
    },

    testalert: function (what, param1, param2) {
      _qaLog(`FC / testalert: what=${what} param1: ${param1} param2: ${param2}`);
      let result = null;
      switch (what) {
        // jshint ignore:start
        case "alert":
          alert(param1);
          break;
        case "confirm":
          result = confirm(param1);
          break;
        case "prompt":
          result = prompt(param1, param2);
          break;
        // jshint ignore:end
      }
      return [result];
    },

    /**
     * Check if we are in a mobile context or not
     * @returns 1 if we are mobile 0 otherwise
     */
    ismobile: function () {
      _qaLog(`FC / isMobile: ${cls.WindowHelper.isMobile()}`);
      return [cls.WindowHelper.isMobile() ? 1 : 0];
    },

    /**
     * Get a theme variable value from ThemeService
     * @param {string} variableName the variable name
     * @returns {string} the corresponding value
     */
    getThemeVariableValue: function (variableName) {
      return [gbc.ThemeService.getValue(variableName)];
    },

    /**
     * Set a theme variable value in ThemeService
     * @param {string} variableName the variable name
     * @param {string} variableValue the new value for this variable
     */
    setThemeVariableValue: function (variableName, variableValue) {
      gbc.ThemeService.setValue(variableName, variableValue);
      return [];
    }
  };
});
